import type { RiqueMessage } from '@/lib/riqueApi';
import type { RiqueVisualization } from '@/components/Rique/RiqueRichContent';
import { SYSTEM_VERSION } from '@/constants/version';
import { formatCurrency } from '@/lib/formatters';

function formatExportBarValue(value: number, format?: 'currency' | 'number'): string {
  if (format === 'currency') return formatCurrency(value);
  return value.toLocaleString('pt-BR');
}

function vizToHtml(viz: RiqueVisualization): string {
  if (viz.type === 'kpi') {
    const rows = viz.items
      .map((i) => `<tr><td>${escapeHtml(String(i.label))}</td><td><strong>${escapeHtml(String(i.value))}</strong></td></tr>`)
      .join('');
    return `<h4>${escapeHtml(viz.title ?? 'Indicadores')}</h4><table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;margin:8px 0">${rows}</table>`;
  }
  if (viz.type === 'bar') {
    const rows = viz.items
      .map(
        (i) =>
          `<tr><td>${escapeHtml(i.name)}</td><td>${escapeHtml(formatExportBarValue(i.value, viz.valueFormat))}</td></tr>`
      )
      .join('');
    return `<h4>${escapeHtml(viz.title)}</h4><table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr><th>Item</th><th>Valor</th></tr></thead><tbody>${rows}</tbody></table>`;
  }
  if (viz.type === 'table') {
    const head = viz.columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
    const body = viz.rows.map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`).join('');
    return `<h4>${escapeHtml(viz.title)}</h4><table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  }
  return '';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildZehMotocaExportHtml(options: {
  title: string;
  messages: RiqueMessage[];
  authorName?: string;
  companyName?: string;
}): string {
  const { title, messages, authorName, companyName } = options;
  const generatedAt = new Date().toLocaleString('pt-BR');

  const body = messages
    .map((m) => {
      const role = m.role === 'USER' ? 'Você' : 'Rique.Ia';
      const vizHtml =
        m.metadata?.visualizations?.map((v) => vizToHtml(v)).join('') ??
        '';
      return `
        <section style="margin-bottom:16px;padding:12px;border-radius:8px;background:${m.role === 'USER' ? '#ecfdf5' : '#f8fafc'};border:1px solid #e2e8f0">
          <div style="font-size:11px;font-weight:600;color:#64748b;margin-bottom:6px">${role}</div>
          <div style="white-space:pre-wrap;font-size:13px;line-height:1.5;color:#0f172a">${escapeHtml(m.content)}</div>
          ${vizHtml}
        </section>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} — Zeh Motoca</title>
  <style>
    @media print { body { margin: 0; } }
    body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; color: #0f172a; margin: 24px; }
  </style>
</head>
<body>
  <header style="border-bottom:3px solid #16a34a;padding-bottom:16px;margin-bottom:24px">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px">
      <div>
        <div style="font-size:22px;font-weight:800;color:#15803d">Zeh Motoca</div>
        <div style="font-size:13px;color:#64748b;margin-top:2px">PortalFly · Relatório Rique.Ia</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#64748b">
        <div>v${SYSTEM_VERSION}</div>
        <div>${escapeHtml(generatedAt)}</div>
      </div>
    </div>
    <h1 style="font-size:18px;margin:16px 0 4px">${escapeHtml(title)}</h1>
    ${companyName ? `<p style="margin:0;font-size:12px;color:#475569">Empresa: ${escapeHtml(companyName)}</p>` : ''}
    ${authorName ? `<p style="margin:4px 0 0;font-size:12px;color:#475569">Gerado por: ${escapeHtml(authorName)}</p>` : ''}
  </header>
  <main>${body}</main>
  <footer style="margin-top:32px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;text-align:center">
    Corps Teknologi · Zeh Motoca · Documento gerado automaticamente pelo assistente Rique.Ia
  </footer>
</body>
</html>`;
}

export function exportConversationPdf(options: Parameters<typeof buildZehMotocaExportHtml>[0]): void {
  printHtmlDocument(buildZehMotocaExportHtml(options));
}

export function exportTableCsv(title: string, columns: string[], rows: string[][]): void {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [columns.map(escape).join(',')];
  for (const row of rows) lines.push(row.map(escape).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function printHtmlDocument(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = iframe.contentDocument ?? frameWindow?.document;
  if (!frameWindow || !frameDoc) {
    iframe.remove();
    throw new Error('Não foi possível preparar a exportação do PDF.');
  }

  const cleanup = () => {
    iframe.remove();
  };

  frameWindow.addEventListener('afterprint', cleanup, { once: true });

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const triggerPrint = () => {
    frameWindow.focus();
    frameWindow.print();
    window.setTimeout(cleanup, 60_000);
  };

  if (frameDoc.readyState === 'complete') {
    window.setTimeout(triggerPrint, 150);
  } else {
    iframe.onload = () => window.setTimeout(triggerPrint, 150);
  }
}

export function buildTablePdfHtml(title: string, columns: string[], rows: string[][]): string {
  const generatedAt = new Date().toLocaleString('pt-BR');
  const head = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
  const body = rows
    .map((row) => `<tr>${columns.map((_, i) => `<td>${escapeHtml(row[i] ?? '')}</td>`).join('')}</tr>`)
    .join('');
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} — Zeh Motoca</title>
  <style>
    @media print { body { margin: 0; } }
    body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; color: #0f172a; margin: 24px; }
    table { border-collapse: collapse; width: 100%; font-size: 12px; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
    th { background: #f1f5f9; font-weight: 600; }
    tr:nth-child(even) td { background: #f8fafc; }
  </style>
</head>
<body>
  <header style="border-bottom:3px solid #16a34a;padding-bottom:16px;margin-bottom:20px">
    <div style="font-size:20px;font-weight:800;color:#15803d">Zeh Motoca</div>
    <div style="font-size:12px;color:#64748b">PortalFly · Rique.Ia · v${SYSTEM_VERSION}</div>
    <h1 style="font-size:16px;margin:12px 0 0">${escapeHtml(title || 'Tabela')}</h1>
    <p style="margin:4px 0 0;font-size:11px;color:#64748b">${escapeHtml(generatedAt)} · ${rows.length} linha(s)</p>
  </header>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
  <footer style="margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;text-align:center">
    Corps Teknologi · Documento gerado pelo assistente Rique.Ia
  </footer>
</body>
</html>`;
}

export function exportTablePdf(title: string, columns: string[], rows: string[][]): void {
  printHtmlDocument(buildTablePdfHtml(title, columns, rows));
}

/** Texto simples da tabela para compartilhar / copiar. */
export function formatTablePlainText(title: string, columns: string[], rows: string[][]): string {
  const header = columns.join(' | ');
  const sep = columns.map(() => '---').join(' | ');
  const body = rows.map((row) => columns.map((_, i) => row[i] ?? '').join(' | ')).join('\n');
  const titleLine = title.trim() ? `${title.trim()}\n\n` : '';
  return `${titleLine}${header}\n${sep}\n${body}\n\n— Rique.Ia · Zeh Motoca`;
}

/**
 * Compartilha a tabela (Web Share API) ou copia para a área de transferência.
 * Retorna o canal usado.
 */
export async function shareTableContent(
  title: string,
  columns: string[],
  rows: string[][]
): Promise<'share' | 'clipboard'> {
  const text = formatTablePlainText(title, columns, rows);
  const shareTitle = title.trim() || 'Tabela Rique.Ia';

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: shareTitle, text });
      return 'share';
    } catch (err) {
      // Usuário cancelou — não cai no clipboard
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return 'clipboard';
  }

  throw new Error('Compartilhamento não disponível neste navegador.');
}
